import { DateTime } from "luxon";
import { BIDSTREAM_TIME_ZONE } from "../../../../models/looker/modules/bidstream";
export const DEFAULT_WINDOW_HOURS = 24;
// Includes minutes, not just the date - day-only formatting (the original
// "yyyy/MM/dd to yyyy/MM/dd" convention, matched to PACING_QUERY, see
// ../pacing/index.ts) truncated to whichever calendar day(s) a window fell
// within, so a short window like "Last 6 hours" silently over-fetched up to
// the full day(s) it touched. Minute precision requests exactly the window
// asked for. Looker's smart date filter accepts "yyyy/MM/dd HH:mm to
// yyyy/MM/dd HH:mm" for absolute ranges (its documented datetime format),
// matching the flexibility already confirmed on this same field via Trend's
// relative "N hours"/"N days" expressions (see bidstream/trend.ts).
//
// Pinned to BIDSTREAM_TIME_ZONE, not the process/viewer's local zone: this
// feeds flt_analysis_interval on the same explore_bidstream explore that
// buildTrendBucketKeys() pins for the same reason (see bidstream.ts) - the
// explore's own time dimensions render in New York wall-clock, so a filter
// built in any other zone would be silently offset from what the explore
// expects.
export const formatDateRange = (dates) => dates
    .map((date) => DateTime.fromJSDate(date).setZone(BIDSTREAM_TIME_ZONE).toFormat("yyyy/MM/dd HH:mm"))
    .join(" to ");
// Floors "now" to the top of the hour or day (per `unit`), so an hour-only
// floor doesn't leave a "7 day" window spanning 8 calendar dates. Floored in
// BIDSTREAM_TIME_ZONE since day boundaries shift across timezones.
export const defaultDateRange = (windowHours = DEFAULT_WINDOW_HOURS, unit = "hour") => {
    const end = DateTime.now().setZone(BIDSTREAM_TIME_ZONE).startOf(unit);
    return [end.minus({ hours: windowHours }).toJSDate(), end.toJSDate()];
};
// "The previous equivalent time period" per PM - the same-length window
// immediately preceding the current one, not an all-time-prior aggregate.
export const priorPeriodDateRange = (dates) => {
    const [start, end] = dates;
    const durationMs = end.getTime() - start.getTime();
    return [new Date(start.getTime() - durationMs), start];
};
// For a DATE-typed Looker field (no time-of-day), e.g. vw_deals.validation_date.
// Looker's range end is already exclusive (see parseDateFilter in
// models/looker/modules/inventory/index.ts), matching what `end` already
// means here in the common case - except when `end` falls mid-day (an
// hour-floored window, e.g. Last 6/24 hours - see constants.ts's
// timeRangeToDates), where that day is only partly covered and rounding
// up by one day is the closer approximation, given this field can't
// express less than a full day. Bump only in that case: bumping an
// already-exact midnight `end` (e.g. Last 7 days) would wrongly add a
// day the window never touches.
export const formatDayRange = (dates) => {
    const [start, end] = dates;
    const endInZone = DateTime.fromJSDate(end).setZone(BIDSTREAM_TIME_ZONE);
    const endIsExactlyMidnight = endInZone.equals(endInZone.startOf("day"));
    const formattedStart = DateTime.fromJSDate(start)
        .setZone(BIDSTREAM_TIME_ZONE)
        .toFormat("yyyy/MM/dd");
    const formattedEnd = (endIsExactlyMidnight ? endInZone : endInZone.plus({ day: 1 })).toFormat("yyyy/MM/dd");
    return `${formattedStart} to ${formattedEnd}`;
};
//# sourceMappingURL=dateRange.js.map